import React, { useState, useRef } from 'react';
import { SwatchBook, CopyPlus, Trash2, MoreHorizontal } from 'lucide-react';
import { logger } from '../../utils/logger';
import { ColorPicker } from './ColorPicker';

interface NodeWrapperProps {
  id: string;
  position: { x: number; y: number };
  size: { width: number | 'auto'; height: number | 'auto' };
  zIndex: number;
  frameStyle: 'browser' | 'none' | 'message' | 'notepad' | 'solid' | 'window';
  nodeType?: 'text' | 'image' | 'video' | 'iframe';
  isEditing: boolean;
  isActive: boolean;
  backgroundColor?: string;
  workInProgress?: boolean;
  borderRadius?: number;
  iframeZoom?: number;
  isAnimatingIn?: boolean;
  animationDelay?: number;
  onActivate?: () => void;
  onPositionChange?: (position: { x: number; y: number }) => void;
  onSizeChange?: (size: { width: number; height: number }) => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  onFrameToggle?: () => void;
  onBackgroundColorChange?: (color: string) => void;
  onWorkInProgressToggle?: () => void;
  onBorderRadiusChange?: (radius: number) => void;
  onIframeZoomChange?: (zoom: number) => void;
  children: React.ReactNode;
}

export const NodeWrapper: React.FC<NodeWrapperProps> = ({
  id,
  position,
  size,
  zIndex,
  frameStyle,
  nodeType,
  isEditing,
  isActive,
  backgroundColor,
  workInProgress,
  borderRadius,
  iframeZoom,
  isAnimatingIn = false,
  animationDelay = 0,
  onActivate,
  onPositionChange,
  onSizeChange,
  onDelete,
  onDuplicate,
  onFrameToggle,
  onBackgroundColorChange,
  onWorkInProgressToggle,
  onBorderRadiusChange,
  onIframeZoomChange,
  children
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState<'nw' | 'ne' | 'sw' | 'se' | null>(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [initialPosition, setInitialPosition] = useState(position);
  const [initialSize, setInitialSize] = useState(size);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const nodeRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isEditing || isResizing) return;

    const target = e.target as HTMLElement;
    if (target.closest('.resize-handle') || target.closest('.node-controls')) return;

    // Don't start dragging if clicking on input/textarea/contentEditable elements
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;
    if (target.isContentEditable || target.closest('[contenteditable="true"]')) return;

    onActivate?.();

    const currentPosition = { x: Number(position.x), y: Number(position.y) };
    logger.debug('drag', `Started dragging node ${id}`, { position: currentPosition });
    setIsDragging(true);
    setIsHovered(false);
    setDragStart({ x: e.clientX, y: e.clientY });
    setInitialPosition(currentPosition);
    e.preventDefault();
  };

  const handleResizeStart = (e: React.MouseEvent, direction: 'nw' | 'ne' | 'sw' | 'se') => {
    if (!isEditing) return;

    e.stopPropagation();
    e.preventDefault();

    onActivate?.();

    setIsResizing(true);
    setIsHovered(false);
    setResizeDirection(direction);
    setDragStart({ x: e.clientX, y: e.clientY });

    // If size is auto, convert to fixed size based on current dimensions
    if (nodeRef.current && (size.width === 'auto' || size.height === 'auto')) {
      const rect = nodeRef.current.getBoundingClientRect();

      const fixedWidth = (rect.width / window.innerWidth) * 100;
      const fixedHeight = (rect.height / window.innerHeight) * 100;

      setInitialSize({ width: fixedWidth, height: fixedHeight });
      setInitialPosition(position);
      // Immediately update the parent with the fixed size
      onSizeChange?.({ width: fixedWidth, height: fixedHeight });
    } else {
      setInitialSize(size);
      setInitialPosition(position);
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging && nodeRef.current) {
      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;

      const parentWidth = nodeRef.current.parentElement?.offsetWidth || window.innerWidth;
      const parentHeight = nodeRef.current.parentElement?.offsetHeight || window.innerHeight;

      const newX = initialPosition.x + (deltaX / parentWidth) * 100;
      const newY = initialPosition.y + (deltaY / parentHeight) * 100;

      onPositionChange?.({ x: newX, y: newY });
    }

    if (isResizing && nodeRef.current && resizeDirection) {
      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;

      const initialWidth = typeof initialSize.width === 'number' ? initialSize.width : 60;
      const initialHeight = typeof initialSize.height === 'number' ? initialSize.height : 30;

      let newWidth = initialWidth;
      let newHeight = initialHeight;
      let newX = initialPosition.x;
      let newY = initialPosition.y;

      // Calculate size changes based on corner direction
      switch (resizeDirection) {
        case 'se': // bottom-right
          newWidth = initialWidth + (deltaX / window.innerWidth) * 100;
          newHeight = initialHeight + (deltaY / window.innerHeight) * 100;
          break;
        case 'sw': // bottom-left
          newWidth = initialWidth - (deltaX / window.innerWidth) * 100;
          newHeight = initialHeight + (deltaY / window.innerHeight) * 100;
          newX = initialPosition.x + (deltaX / window.innerWidth) * 100 / 2;
          break;
        case 'ne': // top-right
          newWidth = initialWidth + (deltaX / window.innerWidth) * 100;
          newHeight = initialHeight - (deltaY / window.innerHeight) * 100;
          newY = initialPosition.y + (deltaY / window.innerHeight) * 100 / 2;
          break;
        case 'nw': // top-left
          newWidth = initialWidth - (deltaX / window.innerWidth) * 100;
          newHeight = initialHeight - (deltaY / window.innerHeight) * 100;
          newX = initialPosition.x + (deltaX / window.innerWidth) * 100 / 2;
          newY = initialPosition.y + (deltaY / window.innerHeight) * 100 / 2;
          break;
      }

      // Minimum size constraints
      newWidth = Math.max(10, newWidth);
      newHeight = Math.max(5, newHeight);

      onSizeChange?.({ width: newWidth, height: newHeight });

      // Update position for corners that affect position
      if (resizeDirection === 'nw' || resizeDirection === 'ne' || resizeDirection === 'sw') {
        onPositionChange?.({ x: newX, y: newY });
      }
    }
  };

  const handleMouseUp = () => {
    if (isDragging) {
      logger.debug('drag', `Finished dragging node ${id}`, { finalPosition: position });
    }
    setIsDragging(false);
    setIsResizing(false);
    setResizeDirection(null);
  };

  const showControls = isEditing && (isActive || isDragging || isResizing);

  React.useEffect(() => {
    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, dragStart, initialPosition, initialSize]);


  const nodeClasses = `
    slide-item absolute crudo-shadow
    ${frameStyle === 'message' ? 'message-item' : ''}
    ${isDragging ? 'cursor-grabbing' : isEditing ? 'cursor-grab' : ''}
    ${isAnimatingIn ? 'stamp-animating' : ''}
    ${isHovered && isEditing && !isDragging && !isResizing ? 'node-hover-active' : ''}
  `;

  const nodeStyles: React.CSSProperties = {
    left: `${position.x}%`,
    top: `${position.y}%`,
    transform: 'translate(-50%, -50%)',
    width: size.width === 'auto' ? 'auto' : `${size.width}vw`,
    height: size.height === 'auto' ? 'auto' : `${size.height}vh`,
    zIndex,
    userSelect: isDragging ? 'none' : 'auto',
    borderRadius: borderRadius ? `${borderRadius}px` : '8px',
    ...(isAnimatingIn && { animationDelay: `${animationDelay}ms` }),
  };

  return (
    <div
      ref={nodeRef}
      className={nodeClasses}
      style={nodeStyles}
      onMouseDown={handleMouseDown}
      onMouseEnter={() => isEditing && !isDragging && !isResizing && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={(e) => e.stopPropagation()}
      data-item-id={id}
    >
      {children}

      {showControls && (
        <>
          {/* Blue outline box with 20px padding */}
          <div className="node-selection-outline" />

          <div className="node-controls absolute -top-12 right-0 flex flex-row items-center">
            {/* Switch node style */}
            <button
              onClick={onFrameToggle}
              className="w-[32px] h-[32px] flex items-center justify-center border border-gray-300 rounded-l bg-white hover:bg-gray-100 transition-colors cursor-pointer"
              title="Switch node style"
            >
              <SwatchBook size={20} className="text-black" />
            </button>

            {/* Duplicate node */}
            <button
              onClick={onDuplicate}
              className="w-[32px] h-[32px] flex items-center justify-center bg-white border border-l-0 border-gray-300 hover:bg-gray-100 transition-colors"
              title="Duplicate node"
            >
              <CopyPlus size={20} className="text-black" />
            </button>

            {/* Delete node */}
            <button
              onClick={onDelete}
              className="w-[32px] h-[32px] flex items-center justify-center bg-white border border-l-0 border-gray-300 hover:bg-gray-100 transition-colors"
              title="Delete node"
            >
              <Trash2 size={20} className="text-black" />
            </button>

            {/* More settings */}
            <div className="relative">
              <button
                onClick={() => setShowColorPicker(!showColorPicker)}
                className="w-[32px] h-[32px] flex items-center justify-center bg-white border border-l-0 border-gray-300 rounded-r hover:bg-gray-100 transition-colors"
                title="More settings"
              >
                <MoreHorizontal size={20} className="text-black" />
              </button>

              {showColorPicker && (
                <div className="absolute top-10 right-0 z-50">
                  <ColorPicker
                    currentColor={backgroundColor}
                    workInProgress={workInProgress}
                    borderRadius={borderRadius}
                    iframeZoom={iframeZoom}
                    showColorPicker={nodeType === 'text' && (frameStyle === 'browser' || frameStyle === 'solid' || frameStyle === 'message')}
                    showBorderRadius={frameStyle !== 'message'}
                    showIframeZoom={nodeType === 'iframe'}
                    onColorSelect={(color) => {
                      onBackgroundColorChange?.(color);
                      setShowColorPicker(false);
                    }}
                    onWorkInProgressToggle={() => {
                      onWorkInProgressToggle?.();
                      setShowColorPicker(false);
                    }}
                    onBorderRadiusChange={(radius) => {
                      onBorderRadiusChange?.(radius);
                    }}
                    onIframeZoomChange={(zoom) => {
                      onIframeZoomChange?.(zoom);
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Corner resize handles - square shapes centered on corners */}
          <div
            className="resize-handle resize-handle-nw"
            onMouseDown={(e) => handleResizeStart(e, 'nw')}
          ></div>
          <div
            className="resize-handle resize-handle-ne"
            onMouseDown={(e) => handleResizeStart(e, 'ne')}
          ></div>
          <div
            className="resize-handle resize-handle-sw"
            onMouseDown={(e) => handleResizeStart(e, 'sw')}
          ></div>
          <div
            className="resize-handle resize-handle-se"
            onMouseDown={(e) => handleResizeStart(e, 'se')}
          ></div>
        </>
      )}
    </div>
  );
};